import React, { Suspense, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch } from '../services/apiClient';
import './SettingsPage.css';

// Lazy load tab components
const LLMSettingsPage = React.lazy(() => import('./LLMSettingsPage'));
const SSOSettingsPage = React.lazy(() => import('./SSOSettingsPage'));
const TeamGovernancePage = React.lazy(() => import('./TeamGovernancePage'));
const EmailDeliverySettingsPage = React.lazy(() => import('./EmailDeliverySettingsPage'));

const SettingsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [canManageTeam, setCanManageTeam] = useState(false);

    // Handle legacy routes: /settings/llm -> /settings?tab=llm, /settings/sso -> /settings?tab=sso
    useEffect(() => {
        if (location.pathname === '/settings/llm' && !location.search.includes('tab=')) {
            navigate('/settings?tab=llm', { replace: true });
        } else if (location.pathname === '/settings/sso' && !location.search.includes('tab=')) {
            navigate('/settings?tab=sso', { replace: true });
        } else if (location.pathname === '/settings/email' && !location.search.includes('tab=')) {
            navigate('/settings?tab=email', { replace: true });
        }
    }, [location.pathname, location.search, navigate]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await apiFetch('/api/auth/status');
                const d = await r.json();
                if (cancelled || !d.authenticated || !d.user) return;
                const role = d.user.role;
                if (role === 'company_admin' || role === 'super_admin') {
                    setCanManageTeam(true);
                }
            } catch {
                /* ignore */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Read tab from URL query params, default to 'llm'
    const params = new URLSearchParams(location.search);
    const activeTab =
        params.get('tab') ||
        (location.pathname === '/settings/sso'
            ? 'sso'
            : location.pathname === '/settings/email'
              ? 'email'
              : 'llm');

    // Validate tab value
    const validTabs = canManageTeam ? ['llm', 'sso', 'team', 'email'] : ['llm', 'sso'];
    const currentTab = validTabs.includes(activeTab) ? activeTab : 'llm';

    const handleTabChange = (tab) => {
        navigate(`/settings?tab=${tab}`, { replace: true });
    };

    // Determine if we can go back (has history)
    const canGoBack = window.history.length > 1;

    return (
        <div className="settings-page">
            <div className="settings-container">
                <div className="settings-header">
                    <h1>Settings</h1>
                    {canGoBack && (
                        <button
                            onClick={() => navigate(-1)}
                            className="back-button"
                            aria-label="Go back"
                        >
                            ← Back
                        </button>
                    )}
                </div>

                {/* Tab Navigation */}
                <div className="settings-tabs">
                    <button
                        className={`settings-tab ${currentTab === 'llm' ? 'active' : ''}`}
                        onClick={() => handleTabChange('llm')}
                        aria-selected={currentTab === 'llm'}
                    >
                        🤖 LLM Settings
                    </button>
                    <button
                        className={`settings-tab ${currentTab === 'sso' ? 'active' : ''}`}
                        onClick={() => handleTabChange('sso')}
                        aria-selected={currentTab === 'sso'}
                    >
                        🔐 SSO Settings
                    </button>
                    {canManageTeam && (
                        <>
                            <button
                                className={`settings-tab ${currentTab === 'team' ? 'active' : ''}`}
                                onClick={() => handleTabChange('team')}
                                aria-selected={currentTab === 'team'}
                            >
                                Team & access
                            </button>
                            <button
                                className={`settings-tab ${currentTab === 'email' ? 'active' : ''}`}
                                onClick={() => handleTabChange('email')}
                                aria-selected={currentTab === 'email'}
                            >
                                Email Configuration
                            </button>
                        </>
                    )}
                </div>

                {/* Tab Content with Suspense for lazy loading */}
                <div className="settings-tab-content">
                    <Suspense
                        fallback={
                            <div className="settings-loading">
                                <div className="loading-spinner"></div>
                                <p>Loading settings...</p>
                            </div>
                        }
                    >
                        {currentTab === 'llm' && <LLMSettingsPage />}
                        {currentTab === 'sso' && <SSOSettingsPage />}
                        {currentTab === 'team' && canManageTeam && <TeamGovernancePage />}
                        {currentTab === 'email' && canManageTeam && <EmailDeliverySettingsPage />}
                    </Suspense>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
